'use client';

import closeIcon from '@/assets/images/menu/icon-close.png';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import { PRODUCT_NAME } from '@/constants';
import type { SiteNotificationItem } from '@/features/notification/types';
import { atom, useAtom } from 'jotai';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import logo from '/public/logo-white.png';
import styles from './CommonMenu.module.scss';

// メニュー開閉
// NOTE: 共通処理に移動したい
const isMenuOpenedAtom = atom(false);
const useIsMenuOpenedAtom = () => useAtom(isMenuOpenedAtom);

export { useIsMenuOpenedAtom };

// コンポーネント
function CommonMenu({ latestNotification }: { latestNotification?: SiteNotificationItem[] }) {
  const [isMenuOpenedValue, setIsMenuOpened] = useIsMenuOpenedAtom();
  const nodeRef = useRef(null);
  const session = useSession();
  const isLoggedIn = !!session?.data?.user?.id;
  const currentPathname = usePathname();
  // リンク遷移で閉じたのか、同一ページで閉じた（×・背景）のかを区別する。
  // 遷移時に旧ページのスクロール位置を復元すると、遷移先ページがその位置に飛んでしまうため。
  const isNavigatingRef = useRef(false);

  const handleLinkClick = () => {
    isNavigatingRef.current = true;
    setIsMenuOpened(false);
  };

  const handleCloseClick = () => {
    isNavigatingRef.current = false;
    setIsMenuOpened(false);
  };

  // ドロワー展開中はメインコンテンツのスクロールを無効化
  useEffect(() => {
    if (isMenuOpenedValue) {
      // 現在のスクロール位置を保存
      const scrollY = window.scrollY;
      const body = document.body;

      // スクロールを無効化（position: fixedで固定）
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';

      return () => {
        body.style.overflow = '';
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        // 同一ページで閉じた時のみ元のスクロール位置に戻す。
        // リンク遷移時は新ページを先頭から表示するため復元しない。
        if (isNavigatingRef.current) {
          isNavigatingRef.current = false;
        } else {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isMenuOpenedValue]);

  const hasNotification = latestNotification && latestNotification.length > 0;

  return (
    <CSSTransition in={isMenuOpenedValue} timeout={200} classNames={styles} nodeRef={nodeRef} appear unmountOnExit>
      <div className={`${styles.componentStyle}`} ref={nodeRef}>
        <div className={styles.menuMainContent}>
          {hasNotification && <div className={styles.notificationSpacer} />}
          <div className={styles.menuHeader}>
            <div className={styles.logoArea}>
              <LoadingLink href={'/'} className={styles.logoLink} onClick={handleLinkClick}>
                <img src={logo.src} alt={PRODUCT_NAME} />
              </LoadingLink>
            </div>
            <button className={styles.closeButton} onClick={handleCloseClick} type='button'>
              <img src={closeIcon.src} alt='メニューを閉じる' className={styles.closeImg} />
            </button>
          </div>
          {/* リンクエリア */}
          <div className={styles.container}>
            {/* セクション1: メインナビゲーション */}
            <div className={styles.menuSection}>
              <ul className={styles.menuList}>
                <li>
                  <LoadingLink href='/' onClick={handleLinkClick}>
                    HOME
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/informations' onClick={handleLinkClick}>
                    お知らせ
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/user-designs' onClick={handleLinkClick}>
                    みんなのデザイン
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink
                    href='/works?sort=created_at_desc'
                    onClick={handleLinkClick}
                    data-testid='works-list-link-mobile'
                  >
                    作品一覧
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/product' onClick={handleLinkClick}>
                    アイテム一覧
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/about' onClick={handleLinkClick} data-testid='about-link-mobile'>
                    ジャンプLABとは
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/faq' onClick={handleLinkClick} data-testid='faq-link-mobile'>
                    FAQ
                  </LoadingLink>
                </li>
                <li>
                  <LoadingLink href='/guide' onClick={handleLinkClick} data-testid='guide-link-mobile'>
                    ご利用ガイド
                  </LoadingLink>
                </li>
              </ul>
              <div className={styles.sectionDivider} />
            </div>
            {/* セクション2: ユーザー関連 */}
            <div className={styles.menuSection}>
              <ul className={styles.menuList}>
                {!isLoggedIn && (
                  <li>
                    <LoadingLink
                      href={`/auth/signin?callbackUrl=${encodeURIComponent(currentPathname)}`}
                      onClick={handleLinkClick}
                    >
                      新規会員登録 / ログイン
                    </LoadingLink>
                  </li>
                )}
                {isLoggedIn && (
                  <>
                    <li>
                      <LoadingLink href='/account' onClick={handleLinkClick}>
                        マイページ
                      </LoadingLink>
                    </li>
                    <li>
                      <LoadingLink href='/account/purchase-history' onClick={handleLinkClick}>
                        注文履歴
                      </LoadingLink>
                    </li>
                    <li>
                      <LoadingLink
                        href='/favorite-material'
                        onClick={handleLinkClick}
                        data-testid='favorite-material-link-mobile'
                      >
                        お気に入りのコマ
                      </LoadingLink>
                    </li>
                  </>
                )}
              </ul>
              <div className={styles.sectionDivider} />
            </div>
            {/* セクション3: お問い合わせ・規約 */}
            <div className={styles.menuSection}>
              <div className={styles.subMenuList}>
                <ul>
                  <li>
                    <LoadingLink href='/contact' onClick={handleLinkClick}>
                      お問い合わせ
                    </LoadingLink>
                  </li>
                  <li>
                    <LoadingLink href='/terms' onClick={handleLinkClick}>
                      利用規約
                    </LoadingLink>
                  </li>
                  <li>
                    <a href='https://fogg.jp/privacy' target='_blank' rel='noopener noreferrer'>
                      個人情報保護方針
                    </a>
                  </li>
                  <li>
                    <LoadingLink href='/purchase-terms' onClick={handleLinkClick}>
                      購買規約
                    </LoadingLink>
                  </li>
                  <li>
                    <LoadingLink href='/design-annotation' onClick={handleLinkClick}>
                      ご購入前の確認事項について
                    </LoadingLink>
                  </li>
                  <li>
                    <LoadingLink href='/policy' onClick={handleLinkClick}>
                      個人情報の取扱いについて
                    </LoadingLink>
                  </li>
                  <li>
                    <LoadingLink href='/tokusho' onClick={handleLinkClick}>
                      特定商取引法に基づく表記
                    </LoadingLink>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CSSTransition>
  );
}

export default CommonMenu;
